import React, { useMemo, useState } from "react";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Button,
  Badge,
  ButtonGroup,
  UncontrolledPopover,
  PopoverHeader,
  PopoverBody,
} from "reactstrap";
import cronstrue from "cronstrue";
import ProcessingButton from "components/ProcessingButton";
import { alert, prompt } from "utils/Prompts";
import SimpleModal from "components/SimpleModal";
import { FaEdit, FaInfoCircle } from "react-icons/fa";
import { useApi } from "utils/API";
import Translation from "utils/Language/Translation";
import Date, { DATE_TIME } from "components/Date";

const OPTIONS: any = {
  seconds: [], // Array.from({ length: 60 }, (_, i) => [i, i]),
  minutes: [], // Array.from({ length: 60 }, (_, i) => [i, i]),
  hours: [], // Array.from({ length: 24 }, (_, i) => [i, i]),
  dom: [], // Array.from({ length: 31 }, (_, i) => [i + 1, i + 1]),
  month: [
    "JAN","FEB","MAR","APR","MAY","JUN",
    "JUL","AUG","SEP","OCT","NOV","DEC"
  ].map((it, idx) => [it, it]),
  dow: ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((it) => [it, it]),
};

const toTokens = (value: string) => {
  if (!value || value === "*") return ["*"];
  return value.split(",");
};

const fromTokens = (tokens: string[]) => tokens.join(",");

export default function CronBuilder({ defaultValue, onSave }: { defaultValue: string, onSave: any }) {
  const [seconds, setSeconds] = useState(["0"]);
  const [minute, setMinute] = useState(["*"]);
  const [hour, setHour] = useState(["*"]);
  const [dayOfMonth, setDayOfMonth] = useState(["*"]);
  const [month, setMonth] = useState(["*"]);
  const [dayOfWeek, setDayOfWeek] = useState(["*"]);

  const cronExpression = useMemo(() => {
    return [seconds, minute, hour, dayOfMonth, month, dayOfWeek]
      .map(fromTokens)
      .join(" ");
  }, [seconds, minute, hour, dayOfMonth, month, dayOfWeek]);

  const humanReadable = useMemo(() => {
    try {
      return cronstrue.toString(cronExpression, { use24HourTimeFormat: true, verbose: true, logicalAndDayFields: true });
    } catch {
      return "Invalid cron expression";
    }
  }, [cronExpression]);

  const addChip = (setter: any, current: any, value: any) => {
    if (!value) return;
    const next = current.includes("*") ? [] : [...current];
    if (!next.includes(value)) next.push(value);
    setter(next.length ? next : ["*"]);
  };

  const removeChip = (setter: any, current: any, value: any) => {
    const next = current.filter((v: any) => v !== value);
    setter(next.length ? next : ["*"]);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const reset = () => {
    setSeconds(["0"]);
    setMinute(["*"]);
    setHour(["*"]);
    setDayOfMonth(["*"]);
    setMonth(["*"]);
    setDayOfWeek(["*"]);
  };

  const presetEveryXMinutes = (x: any) => {
    setSeconds(["0"]);
    setMinute([`*/${x}`]);
    setHour(["*"]);
    setDayOfMonth(["*"]);
    setMonth(["*"]);
    setDayOfWeek(["*"]);
  };

  const presetEveryXHours = (x: any) => {
    setSeconds(["0"]);
    setMinute(["0"]);
    setHour([`*/${x}`]);
    setDayOfMonth(["*"]);
    setMonth(["*"]);
    setDayOfWeek(["*"]);
  };

  const parseCron = (cronInput: string) => {
    if (!cronInput) return;

    let parts = cronInput.trim().split(/\s+/);
    if (parts.length === 5) parts = ["0", ...parts];
    if (parts.length !== 6) return;

    const [s, m, h, dom, mon, dow] = parts;

    setSeconds(toTokens(s));
    setMinute(toTokens(m));
    setHour(toTokens(h));
    setDayOfMonth(toTokens(dom));
    setMonth(toTokens(mon));
    setDayOfWeek(toTokens(dow));
  };

  const FieldEditor = ({ fieldKey, label, value, setValue, options }: any) => {
    const [input, setInput] = useState("");

    let list = null
    let listId = null
    if (options && options.length > 0) {
      listId = label + "-list"
      list = (
        <datalist id={listId}>
          {options.map(([v, label]: any) => (
            <option
              key={v}
              value={v}
            >
              {label}
            </option>
          ))}
        </datalist>  
      )
    }

    return (
      <FormGroup>
        <Label>
          {label}
          {' '}
          <FaInfoCircle id={fieldKey+"-info"}/>
          <UncontrolledPopover target={fieldKey+"-info"} trigger="legacy">
            <PopoverHeader>
              How to
            </PopoverHeader>
            <PopoverBody>
              <ul>
                <li>To use a range use '-' eg: 7-19</li>
                <li>To use multiple entries use a ',' eg: 7, 8, 9, 12</li>
                <li>To use an interval use {'<start at>'}/{'<skip number>'} eg: */5, 5/15, */30</li>
                <li>To signify the nth last day of the month use L-n, or simply L for the last day</li>
              </ul>
            </PopoverBody>
          </UncontrolledPopover>
        </Label>

        <div className="d-flex flex-wrap gap-1 mb-2">
          {value.map((v: any) => (
            <Badge
              key={v}
              color="primary"
              style={{ cursor: "pointer" }}
              onClick={() => removeChip(setValue, value, v)}
            >
              {v} ✕
            </Badge>
          ))}
        </div>

        <div className="d-flex gap-2">
          {list}
          <Input
            list={listId}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addChip(setValue, value, input);
                setInput("");
              }
            }}
          />
          <Button
            size="sm"
            onClick={() => {
              addChip(setValue, value, input);
              setInput("");
            }}
          >
            Add
          </Button>
        </div>
      </FormGroup>
    );
  };

  const api = useApi()

  return (
    <SimpleModal
      title={<span>Advanced Cron Builder</span>}
      fullscreen
      trigger={open => (
        <Button onClick={open}>
          <FaEdit/>
        </Button>
      )}
      onOpen={() => {
        parseCron(defaultValue)
      }}
      buttons={close => [
        <ProcessingButton onClick={async () => {
          const input = await prompt('cron expression', '')
          parseCron(input)
        }}>
          parse expression
        </ProcessingButton>,
        <ProcessingButton onClick={async () => {
          const next = await api.system.testCron(cronExpression)
          if (!next.next) {
            await alert(<Translation name="T.misc.cronTestResultNoNext" />)
          } else {
            await alert(
              <>
                <Translation name="T.misc.cronTestResult" params={{ next: <Date val={next.next} format={DATE_TIME} />}}/>
                {next.nextXMoments.length > 0 && (
                  <ol>
                    {next.nextXMoments.map((it, idx) => <li key={idx}><Date val={it} format={DATE_TIME} /></li>)}
                  </ol>
                )}
              </>
            )
          }
        }}>
          <Translation name="T.misc.test" />
        </ProcessingButton>,
        <ProcessingButton color='primary' onClick={() => {
          onSave(cronExpression)
          close()
        }}>
          <Translation name="T.misc.save"/>
        </ProcessingButton>
      ]}
    >
      <div className="mb-3">
        <strong>Quick Presets:</strong>
        {' '}
        <ButtonGroup className="ms-2">
          <Button size="sm" onClick={() => presetEveryXMinutes(5)}>
            Every 5 min
          </Button>
          <Button size="sm" onClick={() => presetEveryXMinutes(15)}>
            Every 15 min
          </Button>
          <Button size="sm" onClick={() => presetEveryXHours(1)}>
            Every 1 hour
          </Button>
          <Button size="sm" onClick={() => parseCron("0 0 0 1 1 *")}>
            Yearly
          </Button>
          <Button size="sm" onClick={() => parseCron("0 0 0 1 * *")}>
            monthly
          </Button>
          <Button size="sm" onClick={() => parseCron("0 0 0 * * SUN")}>
            weekly
          </Button>
          <Button size="sm" onClick={() => parseCron("0 0 0 * * *")}>
            daily
          </Button>
          <Button size="sm" onClick={() => parseCron("0 0 * * * *")}>
            hourly
          </Button>
        </ButtonGroup>
      </div>

      <Form>
        <Row>
          <Col md={2}>
            <FieldEditor
              fieldKey="second"
              label="Seconds"
              value={seconds}
              setValue={setSeconds}
              options={OPTIONS.seconds}
            />
          </Col>
          <Col md={2}>
            <FieldEditor
              fieldKey="minute"
              label="Minute"
              value={minute}
              setValue={setMinute}
              options={OPTIONS.minutes}
            />
          </Col>
          <Col md={2}>
            <FieldEditor 
              fieldKey="hour"
              label="Hour" 
              value={hour} 
              setValue={setHour}
              options={OPTIONS.hours}
            />
          </Col>
          <Col md={2}>
            <FieldEditor
              fieldKey="dom"
              label="Day of Month"
              value={dayOfMonth}
              setValue={setDayOfMonth}
              options={OPTIONS.dom}
            />
          </Col>
          <Col md={2}>
            <FieldEditor
              fieldKey="month"
              label="Month" 
              value={month} 
              setValue={setMonth}
              options={OPTIONS.month}
            />
          </Col>
          <Col md={2}>
            <FieldEditor
              fieldKey="dow"
              label="Day of Week"
              value={dayOfWeek}
              setValue={setDayOfWeek}
              options={OPTIONS.dow}
            />
          </Col>
        </Row>
      </Form>

      <hr />

      <div className="d-flex flex-column gap-2">
        <Badge color="dark" className="p-2" style={{ fontSize: 14 }}>
          {cronExpression}
        </Badge>

        <div>
          <strong>Description:</strong>
          <div className="text-muted">{humanReadable}</div>
        </div>
      </div>
    </SimpleModal>
  );
}
